import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, IconButton, Paper, Stack,
  Grid, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService, Card as ApiCard } from '../services/paymentService';
import AddCardModal from './AddCardModal';

const CARD_TYPE_DISPLAY: Record<string, string> = {
  VIS: 'VISA',
  MST: 'MASTERCARD',
  AME: 'AMEX',
  JCB: 'JCB',
  UNP: 'UNIONPAY',
  DIS: 'DISCOVER',
};

const CARD_COLORS: Record<string, string> = {
  VISA:       '#1A1F71', // navy blue
  MASTERCARD: '#1C1C1C', // dark charcoal
  AMEX:       '#006FCF', // American Express blue
  JCB:        '#003087', // JCB blue
  UNIONPAY:   '#C0271D', // UnionPay red
  DISCOVER:   '#E65C00', // Discover orange
};

// --- ส่วนที่ 1: บัตรที่บันทึกไว้ (ปรับปรุงให้ตรงกลาง) ---
const PaymentCard = ({ type, lastFour, onDelete }: { type: string, lastFour: string, onDelete: () => void }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Card variant="outlined" sx={{ height: 160, position: 'relative', borderRadius: 2 }}>
      <CardContent sx={{ 
        display: 'flex', 
        alignItems: 'center', // จัดกึ่งกลางแนวตั้งสำหรับทุก Element ในนี้
        height: '100%', 
        p: '0 24px !important', // บังคับ Padding ซ้ายขวาให้เท่ากัน และเอาด้านบนลูออก
        gap: 3 
      }}>
        {/* รูปหน้าบัตร */}
        <Box sx={{ 
          width: 140, 
          height: 90, 
          bgcolor: CARD_COLORS[type] ?? '#002b5c',
          borderRadius: 1.5, 
          position: 'relative', 
          flexShrink: 0 
        }}>
          <Typography sx={{ color: 'white', position: 'absolute', bottom: 8, right: 10, fontSize: '0.7rem' }}>
            {lastFour}
          </Typography>
        </Box>

        {/* ส่วนข้อมูลบัตร - ใช้ Stack เพื่อให้จัดการระยะห่างระหว่างบรรทัดง่ายขึ้น */}
        <Stack direction="column" justifyContent="center" spacing={0.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {type}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1, letterSpacing: 0.5 }}>
            •••• •••• •••• {lastFour}
          </Typography>
        </Stack>

        <IconButton 
          onClick={onDelete}
          sx={{ position: 'absolute', bottom: 12, right: 12 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </CardContent>
    </Card>
  </Grid>
);

// --- AddCardButton Component ---
const AddCardButton = ({ onClick }: { onClick: () => void }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Paper 
      variant="outlined" 
      onClick={onClick}
      sx={{ 
        height: 160, display: 'flex', alignItems: 'center', px: 3,
        cursor: 'pointer', borderRadius: 2, '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={3}>
        <Box sx={{ 
          width: 140, height: 90, border: '1px solid', borderColor: 'divider',
          borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <AddIcon color="action" />
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>Add new card</Typography>
      </Stack>
    </Paper>
  </Grid>
);

// --- Types ---
type PaymentMethodProps = {
  children?: React.ReactNode;
};

// --- Main Component ---
const PaymentMethod: React.FC<PaymentMethodProps> = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cards'],
    queryFn: paymentService.getCards,
  });

  const deleteMutation = useMutation({
    mutationFn: (card_id: string) => paymentService.deleteCard(card_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards'] });
    },
  });

  const cards: ApiCard[] = data?.cards ?? [];

  const handleOpenModal = () => {
    setModalKey(prev => prev + 1);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  return (
    <Box sx={{ mx: "auto", mt: 4, mr: 50 }}>
      <Typography variant="h3" align="center" sx={{ mb: 6 }}>
        My Account
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        Payment Method
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ mb: 3 }}>
          Failed to load payment methods. Please try again.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {cards.map((card) => (
            <PaymentCard
              key={card.id}
              type={CARD_TYPE_DISPLAY[card.card_type] ?? card.card_type}
              lastFour={card.masked_number.slice(-4)}
              onDelete={() => handleDelete(card.id)}
            />
          ))}
          {cards.length < 5 && <AddCardButton onClick={handleOpenModal} />}
        </Grid>
      )}

      <AddCardModal
        key={modalKey}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default PaymentMethod;
